import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { FileText, Download, Loader2, CheckCircle2, GraduationCap } from "lucide-react";

const SECTION_ORDER = [
  { key: "fundamentacion", label: "I. Fundamentación" },
  { key: "objetivo_general", label: "II. Objetivo General" },
  { key: "objetivos_especificos", label: "III. Objetivos Específicos" },
  { key: "principios_metodologicos", label: "IV. Principios Metodológicos" },
  { key: "secuencia_etapas", label: "V. Secuencia de Etapas" },
  { key: "nodos_transferencia", label: "Nodos de Transferencia" },
  { key: "estrategias_permanentes", label: "VI. Estrategias Permanentes" },
  { key: "indicadores_avance", label: "VII. Indicadores de Avance" },
  { key: "consideraciones_finales", label: "VIII. Consideraciones Finales" },
];

const Bullets = ({ items }) => (
  <ul className="space-y-1.5 mt-1">
    {(items || []).map((it, i) => (
      <li key={i} className="flex gap-2 text-[15px] text-[#2b3350] leading-relaxed">
        <span className="text-[#F5B82E] mt-1">•</span><span>{it}</span>
      </li>
    ))}
  </ul>
);

export default function RutaEnsenanzaView() {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [active, setActive] = useState("fundamentacion");
  const [err, setErr] = useState(null);

  useEffect(() => {
    let stop = false;
    const startedAt = Date.now();  // v4.1.1 · tope de espera: antes consultaba para siempre
    let fails = 0;                 // v5.1.0 · reintentos con retroceso si el servidor está ocupado (520/5xx)
    const load = async () => {
      try {
        const { data } = await api.get(`/diagnostics/reports/${reportId}`);
        if (stop) return;
        fails = 0;
        setReport(data.report);
        if (data.report.status === "generating" || data.report.status === "queued") {
          if (Date.now() - startedAt > 15 * 60 * 1000) { setErr("Este informe sigue en progreso. Vuelve a la ficha del alumno en unos minutos."); return; }
          setTimeout(() => { if (!stop) load(); }, document.hidden ? 15000 : 6000);
        }
      } catch (e) {
        const st = e?.response?.status;
        if ((!st || st >= 500) && fails < 6 && !stop) {
          fails += 1;
          setTimeout(() => { if (!stop) load(); }, Math.min(60000, 4000 * 2 ** fails));
          return;
        }
        setErr("El informe aún no está disponible. Intenta de nuevo en unos minutos.");
      }
    };
    load();
    return () => { stop = true; };
  }, [reportId]);

  if (err) return <div className="premium-card p-10 text-center text-[#6E7591]" data-testid="ruta-error">{err}</div>;
  if (!report) return <div className="premium-card p-10 text-center text-[#6E7591]"><Loader2 className="animate-spin inline mr-2" /> Cargando…</div>;
  if (report.status === "generating" || report.status === "queued")
    return <div className="premium-card p-10 text-center text-[#6E7591]" data-testid="ruta-generating"><Loader2 className="animate-spin inline mr-2" /> La IA está redactando la Ruta de Enseñanza…</div>;
  if (report.status === "failed")
    return <div className="premium-card p-10 text-center text-[#6E7591]" data-testid="ruta-failed"><Loader2 className="animate-spin inline mr-2" /> Este informe sigue en progreso. Vuelve a la ficha del alumno en unos minutos.</div>;

  const s = report.sections || {};
  const renderSection = (key) => {
    if (key === "objetivos_especificos") {
      const oe = s.objetivos_especificos || {};
      return (<>
        <h3 className="text-sm font-extrabold text-[#1F2547] mt-2">Área afectivo-emocional</h3>
        <Bullets items={oe.afectivo_emocional} />
        <h3 className="text-sm font-extrabold text-[#1F2547] mt-3">Área cognitiva</h3>
        <Bullets items={oe.cognitivo} />
      </>);
    }
    if (key === "principios_metodologicos" || key === "indicadores_avance") return <Bullets items={s[key]} />;
    if (key === "nodos_transferencia") {
      return (<div className="space-y-3 mt-2" data-testid="ruta-nodos">
        {(s.nodos_transferencia || []).map((nd, i) => (
          <div key={i} className="rounded-xl border border-[#EEE0F0] p-4">
            <h3 className="text-sm font-extrabold text-[#1F2547]">{nd.eje}</h3>
            <p className="text-[15px] text-[#2b3350] mt-1">{(nd.cadena || []).join(" → ")}</p>
          </div>
        ))}
      </div>);
    }
    if (key === "estrategias_permanentes") {
      const ep = s.estrategias_permanentes || {};
      return (<>
        {ep.afectivo_emocionales && <><h3 className="text-sm font-extrabold text-[#1F2547] mt-2">Afectivo-emocionales</h3><Bullets items={ep.afectivo_emocionales} /></>}
        {ep.cognitivas && <><h3 className="text-sm font-extrabold text-[#1F2547] mt-3">Cognitivas</h3><Bullets items={ep.cognitivas} /></>}
        {ep.jasq && <><h3 className="text-sm font-extrabold text-[#1F2547] mt-3">Propias de JASQ®</h3><Bullets items={ep.jasq} /></>}
      </>);
    }
    if (key === "secuencia_etapas") {
      return (<div className="space-y-4 mt-2">
        {(s.secuencia_etapas || []).map((e, i) => (
          <div key={i} className="rounded-xl border border-[#EEE0F0] overflow-hidden" data-testid={`etapa-${e.etapa || i}`}>
            <div className="bg-[#A43790] text-white px-4 py-2 font-extrabold text-sm flex items-center gap-2"><GraduationCap size={15} /> Etapa {e.etapa} — {e.titulo}</div>
            <div className="p-4">
              {e.objetivo && <p className="text-[15px] text-[#2b3350]"><b>Objetivo:</b> {e.objetivo}</p>}
              {e.contenidos && <Bullets items={e.contenidos} />}
              {e.meta_observable && <p className="text-[14px] text-[#1FA971] font-semibold mt-2 flex items-center gap-1.5"><CheckCircle2 size={14} /> {e.meta_observable}</p>}
            </div>
          </div>
        ))}
      </div>);
    }
    // texto simple
    return <div className="text-[15px] text-[#2b3350] leading-relaxed space-y-2 mt-1">{String(s[key] || "").split("\n").filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}</div>;
  };

  return (
    <div className="space-y-5" data-testid="ruta-view">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-black text-[#1F2547]">Ruta de Enseñanza</h1>
          <p className="text-sm text-[#6E7591]">Programa Neuromatemáticas JASQ® · versión {report.version}</p>
        </div>
        {report.pdf_url && (
          <a href={report.pdf_url} target="_blank" rel="noreferrer" data-testid="ruta-pdf-download"
             className="inline-flex items-center gap-2 bg-[#A43790] text-white px-4 py-2 rounded-md text-sm font-bold hover:bg-[#8A2E79]">
            <Download size={15} /> Descargar PDF
          </a>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        <nav className="lg:col-span-1 premium-card p-3 h-max lg:sticky lg:top-4" data-testid="ruta-nav">
          {SECTION_ORDER.filter((sec) => s[sec.key] && !(Array.isArray(s[sec.key]) && !s[sec.key].length)).map((sec) => (
            <button key={sec.key} onClick={() => setActive(sec.key)}
              data-testid={`nav-${sec.key}`}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-semibold mb-1 ${active === sec.key ? "bg-[#A43790] text-white" : "text-[#41496a] hover:bg-[#FAF5FB]"}`}>
              {sec.label}
            </button>
          ))}
        </nav>
        <div className="lg:col-span-3 premium-card p-6" data-testid="ruta-content">
          <h2 className="text-lg font-extrabold text-[#A43790] flex items-center gap-2 mb-1">
            <FileText size={18} /> {SECTION_ORDER.find((x) => x.key === active)?.label}
          </h2>
          {renderSection(active)}
        </div>
      </div>
    </div>
  );
}
